import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";
import {
  ArrowRight,
  Crown,
  ExternalLink,
  LogOut,
  Megaphone,
  Save,
  Trash2,
  Loader2,
  Users,
  UserMinus,
  Image as ImageIcon,
  Upload,
  X,
  Copy,
  Mail,
  Link as LinkIcon,
  Check,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DashboardPage = () => {
  const { user, token, loading, logout, API, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [church, setChurch] = useState(null);
  const [loadingChurch, setLoadingChurch] = useState(true);
  const [form, setForm] = useState({
    name: "",
    description: "",
    accent_color: "#14b8a6",
    welcome_message: "",
  });
  const [saving, setSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: "", body: "" });
  const [posting, setPosting] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const loadChurch = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/churches/mine`, { headers: authHeaders });
      const c = res.data;
      // If signed-in user is not the owner, they are a member — send them to the hub
      if (user && c.owner_id !== user.id) {
        navigate(`/c/${c.slug}`);
        return;
      }
      setChurch(c);
      setForm({
        name: c.name,
        description: c.description || "",
        accent_color: c.accent_color || "#14b8a6",
        welcome_message: c.welcome_message || "",
      });
      const aRes = await axios.get(`${API}/churches/${c.id}/announcements`);
      setAnnouncements(aRes.data);
      setLoadingMembers(true);
      try {
        const mRes = await axios.get(`${API}/churches/${c.id}/members`, {
          headers: authHeaders,
        });
        setMembers(mRes.data);
      } catch (_) {
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    } catch (e) {
      setChurch(null);
    } finally {
      setLoadingChurch(false);
    }
  }, [token, API, user, navigate]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
      return;
    }
    if (user) {
      if (!user.church_id) {
        navigate("/signup");
        return;
      }
      loadChurch();
    }
  }, [user, loading, navigate, loadChurch]);

  const saveChurch = async (e) => {
    e.preventDefault();
    if (!church) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/churches/${church.id}`, form, {
        headers: authHeaders,
      });
      setChurch(res.data);
      toast({ title: "Saved", description: "Your hub has been updated." });
    } catch (err) {
      toast({
        title: "Could not save",
        description: err?.response?.data?.detail || "Try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const postAnnouncement = async (e) => {
    e.preventDefault();
    if (!church) return;
    if (newAnnouncement.title.length < 2 || newAnnouncement.body.length < 2) {
      toast({ title: "Title and body required" });
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(
        `${API}/churches/${church.id}/announcements`,
        newAnnouncement,
        { headers: authHeaders }
      );
      setAnnouncements((prev) => [res.data, ...prev]);
      setNewAnnouncement({ title: "", body: "" });
      toast({ title: "Announcement posted" });
    } catch (err) {
      toast({
        title: "Could not post",
        description: err?.response?.data?.detail || "Try again.",
      });
    } finally {
      setPosting(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`, { headers: authHeaders });
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      toast({
        title: "Could not delete",
        description: err?.response?.data?.detail || "Try again.",
      });
    }
  };

  const removeMember = async (memberId) => {
    if (!church) return;
    if (!window.confirm("Remove this member from your hub?")) return;
    try {
      await axios.delete(`${API}/churches/${church.id}/members/${memberId}`, {
        headers: authHeaders,
      });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      toast({ title: "Member removed" });
    } catch (err) {
      toast({
        title: "Could not remove",
        description: err?.response?.data?.detail || "Try again.",
      });
    }
  };

  const onUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !church) return;
    const okTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast({ title: "Unsupported file", description: "PNG, JPG, or WebP only." });
      e.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: "Too big", description: "Logo must be 2 MB or smaller." });
      e.target.value = "";
      return;
    }
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/churches/${church.id}/logo`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      // Cache-bust the logo url
      setChurch({ ...res.data, logo_url: `${res.data.logo_url}?t=${Date.now()}` });
      toast({ title: "Logo updated" });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || "Try again.",
      });
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const removeLogo = async () => {
    if (!church || !window.confirm("Remove your church logo?")) return;
    try {
      const res = await axios.delete(`${API}/churches/${church.id}/logo`, {
        headers: authHeaders,
      });
      setChurch(res.data);
      toast({ title: "Logo removed" });
    } catch (err) {
      toast({
        title: "Could not remove",
        description: err?.response?.data?.detail || "Try again.",
      });
    }
  };

  const hubUrl = church
    ? `${window.location.origin}/c/${church.slug}`
    : "";

  const copyHubLink = async () => {
    if (!hubUrl) return;
    try {
      await navigator.clipboard.writeText(hubUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast({ title: "Link copied" });
    } catch {
      toast({ title: "Could not copy", description: "Select & copy manually." });
    }
  };

  const emailInviteHref = () => {
    if (!church) return "#";
    const subject = encodeURIComponent(
      `You're invited to join ${church.name} on Good Fruit`
    );
    const body = encodeURIComponent(
      `Hi,\n\nI'd love for you to join our church hub for ${church.name} on Good Fruit.\n\n` +
        `It's where we'll share announcements, prayer requests, and faith-rooted resources for our community.\n\n` +
        `Join here (takes 30 seconds):\n${hubUrl}\n\n` +
        `Blessings,\n${user?.name || "Your Pastor"}`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading || loadingChurch) {
    return (
      <div className="bg-black text-white min-h-screen antialiased">
        <Navbar />
        <div className="pt-40 flex items-center justify-center text-white/60">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your hub…
        </div>
      </div>
    );
  }

  if (!church) {
    return null; // navigation will handle
  }

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 md:pt-32 pb-10 bg-[#04181a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs">
                <Crown className="w-3.5 h-3.5" /> Admin Dashboard
              </div>
              <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
                {church.name}
              </h1>
              <div className="mt-2 text-white/60 text-sm">
                Logged in as {user.name} ·{" "}
                <button
                  onClick={() => {
                    logout();
                    refreshUser();
                    navigate("/");
                  }}
                  className="inline-flex items-center gap-1 text-teal-300 hover:text-teal-200"
                >
                  <LogOut className="w-3 h-3" /> Sign out
                </button>
              </div>
            </div>
            <Link to={`/c/${church.slug}`}>
              <Button
                variant="outline"
                className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-teal-300"
              >
                View public hub
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Invite link card */}
      <section className="bg-[#04181a] pb-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div
            data-testid="invite-card"
            className="rounded-2xl border border-teal-400/25 bg-gradient-to-br from-teal-500/10 to-teal-400/[0.03] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
          >
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.18em] text-teal-300 font-medium">
                Invite link
              </div>
              <div className="mt-1.5 text-sm text-white/70">
                Share this link with members. They&apos;ll get a welcome email and join instantly.
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm overflow-hidden">
                <LinkIcon className="w-4 h-4 text-teal-300 shrink-0" />
                <span
                  data-testid="invite-link-text"
                  className="truncate text-white/85"
                >
                  {hubUrl}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={copyHubLink}
                data-testid="copy-link-button"
                variant="outline"
                className="bg-transparent border-teal-400/40 text-teal-200 hover:bg-teal-500/10 hover:text-teal-100"
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </>
                )}
              </Button>
              <a
                href={emailInviteHref()}
                data-testid="email-invite-button"
                className="inline-flex items-center justify-center gap-2 px-4 h-10 rounded-md bg-teal-400 hover:bg-teal-300 text-black font-semibold text-sm transition"
              >
                <Mail className="w-4 h-4" /> Email invite
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-black py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Settings */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <h2 className="text-xl font-semibold tracking-tight">Hub settings</h2>
              <p className="mt-1 text-sm text-white/55">
                What your members see when they visit your church hub.
              </p>
              <form onSubmit={saveChurch} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm text-white/80 mb-2">Logo</label>
                  <div
                    className="flex items-center gap-4"
                    data-testid="logo-uploader"
                  >
                    <div
                      className="w-16 h-16 rounded-xl border border-white/10 bg-black/40 overflow-hidden flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor: church.logo_url ? "#000" : "#0a0a0a",
                      }}
                    >
                      {church.logo_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${church.logo_url}`}
                          alt={`${church.name} logo`}
                          data-testid="dashboard-logo-preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-white/35" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label
                        className={`inline-flex items-center gap-2 px-3.5 h-9 rounded-md border border-white/15 bg-white/[0.05] text-white text-sm cursor-pointer hover:bg-white/[0.08] ${uploadingLogo ? "opacity-60 pointer-events-none" : ""}`}
                        data-testid="upload-logo-label"
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Upload className="w-3.5 h-3.5" />
                        )}
                        {church.logo_url ? "Replace" : "Upload"}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          onChange={onUploadLogo}
                          data-testid="logo-file-input"
                          className="hidden"
                        />
                      </label>
                      {church.logo_url && (
                        <button
                          type="button"
                          onClick={removeLogo}
                          data-testid="remove-logo-button"
                          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-rose-300 hover:bg-rose-500/10 text-sm"
                        >
                          <X className="w-3.5 h-3.5" /> Remove
                        </button>
                      )}
                      <div className="text-xs text-white/45 w-full">
                        PNG, JPG, or WebP · 2 MB max
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-white/80 mb-2">Church name</label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/80 mb-2">Description</label>
                  <Textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/80 mb-2">Welcome message</label>
                  <Textarea
                    rows={3}
                    value={form.welcome_message}
                    onChange={(e) => setForm((f) => ({ ...f, welcome_message: e.target.value }))}
                    className="bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/80 mb-2">Accent color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.accent_color}
                      onChange={(e) => setForm((f) => ({ ...f, accent_color: e.target.value }))}
                      className="h-10 w-12 rounded-lg border border-white/15 bg-transparent cursor-pointer"
                    />
                    <code className="text-sm text-white/60 font-mono">{form.accent_color}</code>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-teal-400 hover:bg-teal-300 text-black font-semibold h-11 disabled:opacity-60"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </form>
            </div>
          </div>

          {/* Announcements */}
          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-teal-300" />
                <h2 className="text-xl font-semibold tracking-tight">Announcements</h2>
              </div>
              <p className="mt-1 text-sm text-white/55">
                Post updates members see on your hub.
              </p>

              <form onSubmit={postAnnouncement} className="mt-5 space-y-3">
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement((a) => ({ ...a, title: e.target.value }))}
                  placeholder="Title — e.g., Sunday gathering update"
                  className="h-11 bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                />
                <Textarea
                  rows={3}
                  value={newAnnouncement.body}
                  onChange={(e) => setNewAnnouncement((a) => ({ ...a, body: e.target.value }))}
                  placeholder="Write the update your members will read…"
                  className="bg-white/[0.05] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-teal-400"
                />
                <Button
                  type="submit"
                  disabled={posting}
                  className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-10 px-5 disabled:opacity-60"
                >
                  {posting ? "Posting…" : "Post announcement"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </form>

              <div className="mt-8 space-y-3">
                {announcements.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/15 p-6 text-center text-white/45 text-sm">
                    No announcements yet. Post your first to greet your members.
                  </div>
                ) : (
                  announcements.map((a) => (
                    <div
                      key={a.id}
                      className="rounded-xl border border-white/10 bg-black/40 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="font-semibold text-white">{a.title}</div>
                          <div className="mt-1 text-sm text-white/70 whitespace-pre-wrap">
                            {a.body}
                          </div>
                          <div className="mt-2 text-[11px] text-white/40 uppercase tracking-wide">
                            {new Date(a.created_at).toLocaleString()} · {a.author_name}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteAnnouncement(a.id)}
                          aria-label="Delete"
                          data-testid={`delete-announcement-${a.id}`}
                          className="text-white/45 hover:text-rose-300 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Members roster */}
          <div className="lg:col-span-12" data-testid="members-panel">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-teal-300" />
                  <h2 className="text-xl font-semibold tracking-tight">
                    Members
                    <span className="ml-2 text-sm text-white/45 font-normal">
                      {members.length} {members.length === 1 ? "person" : "people"}
                    </span>
                  </h2>
                </div>
                <div className="text-xs text-white/50">
                  Share <code className="text-teal-300">/c/{church.slug}</code> for new members to join.
                </div>
              </div>

              {loadingMembers ? (
                <div className="mt-6 flex items-center gap-2 text-white/55 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading roster…
                </div>
              ) : members.length === 0 ? (
                <div className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-white/45 text-sm">
                  No members yet. Invite people to join at <code>/c/{church.slug}</code>.
                </div>
              ) : (
                <div className="mt-6 divide-y divide-white/5">
                  {members.map((m) => {
                    const isOwner = m.id === church.owner_id;
                    return (
                      <div
                        key={m.id}
                        data-testid={`member-row-${m.id}`}
                        className="flex items-center justify-between gap-3 py-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-black shrink-0"
                            style={{ backgroundColor: church.accent_color }}
                          >
                            {m.name.slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {m.name}
                              {isOwner && (
                                <span className="ml-2 text-[10px] uppercase tracking-wider bg-teal-400/20 text-teal-200 px-1.5 py-0.5 rounded">
                                  Owner
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-white/45 truncate">
                              {m.email} · joined {new Date(m.joined_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        {!isOwner && (
                          <button
                            onClick={() => removeMember(m.id)}
                            aria-label="Remove member"
                            data-testid={`remove-member-${m.id}`}
                            className="text-white/45 hover:text-rose-300 p-1.5 rounded hover:bg-white/5"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default DashboardPage;
